import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X as XIcon,
  FileText,
  FileX,
  CircleDot,
  CircleDashed,
  Disc3,
  Sparkles,
  HelpCircle,
  Car,
  CarFront,
  AlertTriangle,
  Power,
  Cog,
  Wrench,
  Gauge,
  Settings,
  CheckCircle2,
  Smile,
  Frown,
  PartyPopper,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import api from "@/lib/api";
import { CAR_MAKES, CAR_MAKE_NAMES } from "@/lib/carMakes";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  make: "",
  model: "",
  car_type: "",
  car_year: "",
  mileage: "",
  postal_code: "",
  has_registration: "",
  tires: "",
  wheels: "",
  condition: "",
  transmission: "",
  runs: "",
  damage: "",
  missing_parts: "",
  message: "",
};

/* --- cartoon-style icon in colored pastel tile --- */
const Illus = ({ Icon, tone = "blue", spin = false }) => {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-[#0055FF]",
    green: "bg-emerald-50 border-emerald-200 text-emerald-600",
    amber: "bg-amber-50 border-amber-200 text-amber-600",
    red: "bg-red-50 border-red-200 text-red-600",
    slate: "bg-slate-100 border-slate-200 text-slate-600",
  };
  return (
    <div
      className={`h-14 w-14 sm:h-16 sm:w-16 rounded-2xl border-2 grid place-items-center shrink-0 transition-transform group-hover:scale-105 ${tones[tone]} ${spin ? "group-hover:rotate-12" : ""}`}
      aria-hidden="true"
    >
      <Icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2} />
    </div>
  );
};

const STEPS = [
  {
    id: "contact",
    title: "Let's start with your contact details",
    subtitle: "We'll text or call back with a cash offer in minutes.",
    type: "fields",
    fields: [
      { name: "name", label: "Full name", required: true, placeholder: "Jane Doe" },
      { name: "phone", label: "Phone", required: true, placeholder: "(289) 555-0100" },
      { name: "email", label: "Email (optional)", type: "email", placeholder: "you@example.com" },
    ],
    validate: (f) => (f.name.trim() && f.phone.trim() ? null : "Name and phone are required."),
    summary: (f) =>
      [f.name, f.phone, f.email].filter(Boolean).join(" · "),
  },
  {
    id: "vehicle",
    title: "Tell us about your car",
    subtitle: "Pick your make, model, and year so we can price it accurately.",
    type: "make_model_year",
    validate: (f) =>
      f.make && f.model && String(f.car_year).trim()
        ? null
        : "Please pick a make, a model, and enter the year.",
    summary: (f) => `${f.make} ${f.model} · ${f.car_year}`,
  },
  {
    id: "mileage",
    title: "What's the mileage?",
    subtitle: "Approximate is fine.",
    type: "fields",
    fields: [{ name: "mileage", label: "Mileage", placeholder: "e.g. 180000 km" }],
    summary: (f) => f.mileage || "—",
  },
  {
    id: "postal_code",
    title: "Where should we pick it up?",
    subtitle: "Just a postal code is fine for now.",
    type: "fields",
    fields: [{ name: "postal_code", label: "Postal code", required: true, placeholder: "e.g. M5V 3L9" }],
    validate: (f) => (f.postal_code.trim() ? null : "Postal code is required."),
    summary: (f) => f.postal_code,
  },
  {
    id: "has_registration",
    title: "Do you have the car's registration?",
    type: "options",
    name: "has_registration",
    options: [
      { value: "Yes", label: "Yes, I have it", Icon: FileText, tone: "green" },
      { value: "No", label: "No, I don't", Icon: FileX, tone: "amber" },
    ],
  },
  {
    id: "tires",
    title: "Are all 4 tires ready to roll?",
    type: "options",
    name: "tires",
    options: [
      { value: "All 4 ready", label: "Yes — all 4 ready", Icon: CircleDot, tone: "green" },
      { value: "One or more flat", label: "No — one or more are flat", Icon: CircleDashed, tone: "amber" },
      { value: "One or more missing", label: "No — one or more are missing", Icon: XIcon, tone: "red" },
    ],
  },
  {
    id: "wheels",
    title: "Steel or alloy wheels?",
    type: "options",
    name: "wheels",
    options: [
      { value: "Steel", label: "Steel", Icon: Disc3, tone: "slate" },
      { value: "Alloy", label: "Alloy", Icon: Sparkles, tone: "blue" },
      { value: "Not sure", label: "Not sure", Icon: HelpCircle, tone: "amber" },
    ],
  },
  {
    id: "condition",
    title: "Overall condition of the car?",
    type: "options",
    name: "condition",
    options: [
      { value: "Drives", label: "Yes — it drives", Icon: CarFront, tone: "green" },
      { value: "Starts but doesn't drive", label: "It starts but doesn't drive", Icon: Car, tone: "amber" },
      { value: "Doesn't start", label: "Doesn't start", Icon: AlertTriangle, tone: "red" },
    ],
  },
  {
    id: "transmission",
    title: "What kind of transmission?",
    type: "options",
    name: "transmission",
    options: [
      { value: "Automatic", label: "Automatic", Icon: Settings, tone: "blue" },
      { value: "Manual", label: "Manual", Icon: Cog, tone: "slate", spin: true },
    ],
  },
  {
    id: "runs",
    title: "Does it run?",
    type: "options",
    name: "runs",
    options: [
      { value: "Runs", label: "Yes — runs fine", Icon: Power, tone: "green" },
      { value: "Engine issue", label: "No — engine issue", Icon: Gauge, tone: "amber" },
      { value: "Transmission issue", label: "No — transmission issue", Icon: Cog, tone: "red", spin: true },
    ],
  },
  {
    id: "damage",
    title: "Any rust or dents?",
    type: "options",
    name: "damage",
    options: [
      { value: "Major rust or dents", label: "Yes — major rust or dents", Icon: Frown, tone: "red" },
      { value: "Minor rust or dents", label: "Yes — minor rust or dents", Icon: AlertTriangle, tone: "amber" },
      { value: "Just a few scratches", label: "No — just a few scratches", Icon: Smile, tone: "green" },
    ],
  },
  {
    id: "missing_parts",
    title: "Is anything missing from the car?",
    type: "options",
    name: "missing_parts",
    options: [
      { value: "Nothing missing", label: "Nothing missing", Icon: CheckCircle2, tone: "green" },
      { value: "Catalytic converter", label: "Yes — catalytic converter", Icon: AlertTriangle, tone: "amber" },
      { value: "Other parts", label: "Yes — other parts", Icon: Wrench, tone: "slate" },
    ],
  },
  {
    id: "notes",
    title: "Anything else we should know?",
    subtitle: "Optional — accident history, missing keys, title status, etc.",
    type: "textarea",
    name: "message",
    placeholder: "Add any extra detail that might affect the quote...",
  },
];

export default function Quote() {
  const location = useLocation();
  const initial = location.state?.initial || {};
  const [form, setForm] = useState({ ...initialForm, ...initial });
  const [revealed, setRevealed] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const stepRefs = useRef({});

  const total = STEPS.length;
  const progress = useMemo(() => {
    const n = Math.min(revealed, total);
    return Math.round((n / total) * 100);
  }, [revealed, total]);

  // Smooth-scroll to the newest revealed question
  useEffect(() => {
    const el = stepRefs.current[STEPS[revealed - 1]?.id];
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [revealed]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setMake = (make) => setForm((f) => ({ ...f, make, model: "" }));
  const modelOptions = useMemo(
    () => (form.make && CAR_MAKES[form.make] ? CAR_MAKES[form.make] : []),
    [form.make],
  );

  const revealNext = (currentIdx) => {
    if (currentIdx < revealed - 1) return; // editing an old one — don't advance
    if (revealed < total) setRevealed(revealed + 1);
  };

  const advanceFromOption = (stepIdx) => {
    // Called synchronously after picking an option — no validation needed
    // because the click itself is the answer. Fixes the double-click bug
    // where tryAdvance would read stale form state from its closure.
    if (stepIdx < revealed - 1) return;
    if (revealed < total) setRevealed(revealed + 1);
  };

  const tryAdvance = (stepIdx) => {
    const step = STEPS[stepIdx];
    if (step.validate) {
      const err = step.validate(form);
      if (err) {
        toast.error(err);
        return false;
      }
    }
    if (step.type === "options" && !form[step.name]) {
      toast.error("Please pick an option to continue.");
      return false;
    }
    revealNext(stepIdx);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (form.make || form.model) {
        payload.car_type = [form.make, form.model]
          .filter((v) => v && v !== "Other / not listed")
          .join(" ")
          .trim();
      }
      delete payload.make;
      delete payload.model;
      await api.post("/contact", payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessScreen />;

  const allRevealed = revealed >= total;

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="quote-page">
      <Header />

      {/* Sticky progress bar */}
      <div className="sticky top-20 md:top-24 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-3 flex items-center gap-4">
          <span className="text-xs font-mono-plex text-slate-500 shrink-0" data-testid="quote-step-counter">
            {String(Math.min(revealed, total)).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <div className="flex-1 h-1.5 bg-slate-200 overflow-hidden rounded-full">
            <div
              className="h-full bg-[#0055FF] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="quote-progress"
            />
          </div>
          <span className="text-xs font-mono-plex text-slate-700 shrink-0 w-10 text-right">{progress}%</span>
        </div>
      </div>

      <main className="flex-1">
        <section className="max-w-3xl mx-auto px-6 md:px-8 pt-10 pb-24">
          {/* Intro */}
          <div className="mb-14 md:mb-20">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
              Get an offer
            </div>
            <h1
              className="mt-3 font-display font-semibold text-3xl sm:text-5xl md:text-6xl tracking-tight text-slate-900 leading-[1.05]"
              data-testid="quote-intro"
            >
              Selling your scrap car is as easy as…
            </h1>
            <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-xl">
              Answer a few quick questions. We'll text or call back with a
              guaranteed cash offer in minutes.
            </p>
          </div>

          {/* Stacked question bubbles */}
          <div className="space-y-8 md:space-y-10">
            {STEPS.slice(0, revealed).map((step, idx) => {
              const isCurrent = idx === revealed - 1;
              return (
                <div
                  key={step.id}
                  ref={(el) => (stepRefs.current[step.id] = el)}
                  data-testid={`quote-question-${step.id}`}
                  className={`animate-question-in transition-all duration-300 ${
                    isCurrent
                      ? "opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <div
                    className={`bg-white border rounded-3xl px-6 sm:px-12 py-10 sm:py-14 transition-all duration-300 hover:-translate-y-0.5 ${
                      isCurrent
                        ? "border-slate-200 shadow-[0_25px_70px_-15px_rgba(0,85,255,0.22)]"
                        : "border-slate-200 shadow-[0_8px_20px_-10px_rgba(15,23,42,0.08)]"
                    }`}
                  >
                    <QuestionBody
                      step={step}
                      idx={idx}
                      isCurrent={isCurrent}
                      form={form}
                      setField={setField}
                      setMake={setMake}
                      modelOptions={modelOptions}
                      onAdvance={() => tryAdvance(idx)}
                      onOptionPicked={() => advanceFromOption(idx)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit */}
          {allRevealed && (
            <div
              className="mt-16 md:mt-24 animate-question-in"
              data-testid="quote-submit-block"
            >
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="quote-submit"
                className="w-full sm:w-auto inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] disabled:opacity-60 text-white px-10 py-5 text-base sm:text-lg font-medium rounded-full transition-colors"
              >
                {submitting ? "Sending…" : "Get my cash offer"}
                <ArrowRight className="h-5 w-5" />
              </button>
              <p className="mt-4 text-sm text-slate-500">
                Guaranteed offer. Free pickup. Cash on the spot.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/* ---------- Individual question renderer ---------- */
function QuestionBody({
  step,
  idx,
  isCurrent,
  form,
  setField,
  setMake,
  modelOptions,
  onAdvance,
  onOptionPicked,
}) {
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-xs font-mono-plex font-bold">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <h2
          className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl tracking-tight text-slate-900 leading-[1.15]"
          data-testid={`quote-title-${step.id}`}
        >
          {step.title}
        </h2>
      </div>
      {step.subtitle && (
        <p className="mt-3 ml-11 text-slate-600 max-w-2xl">{step.subtitle}</p>
      )}

      <div className="mt-8 ml-0 sm:ml-11">
        {step.type === "fields" && (
          <FieldsInput
            step={step}
            form={form}
            setField={setField}
            isCurrent={isCurrent}
            onEnter={onAdvance}
          />
        )}
        {step.type === "make_model_year" && (
          <MakeModelYear
            form={form}
            setField={setField}
            setMake={setMake}
            modelOptions={modelOptions}
          />
        )}
        {step.type === "options" && (
          <OptionsInput
            step={step}
            form={form}
            setField={setField}
            onPicked={() => onOptionPicked && onOptionPicked()}
          />
        )}
        {step.type === "textarea" && (
          <textarea
            value={form[step.name]}
            onChange={(e) => setField(step.name, e.target.value)}
            rows={5}
            placeholder={step.placeholder}
            data-testid={`quote-textarea-${step.name}`}
            className="w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg"
          />
        )}

        {isCurrent && (step.type === "fields" || step.type === "make_model_year" || step.type === "textarea") && (
          <button
            type="button"
            onClick={onAdvance}
            data-testid={`quote-continue-${step.id}`}
            className="mt-6 inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] text-white px-6 py-3 text-sm font-medium rounded-full transition-colors"
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function FieldsInput({ step, form, setField, isCurrent, onEnter }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {step.fields.map((f, i) => (
        <div
          key={f.name}
          className={step.fields.length === 1 ? "sm:col-span-2" : ""}
        >
          <label className="text-xs font-medium uppercase tracking-widest text-slate-500">
            {f.label}
            {f.required && " *"}
          </label>
          <input
            type={f.type || "text"}
            value={form[f.name]}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder}
            required={f.required}
            autoFocus={isCurrent && i === 0 && !form[f.name]}
            data-testid={`quote-input-${f.name}`}
            className="mt-2 w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onEnter();
              }
            }}
          />
        </div>
      ))}
    </div>
  );
}

function MakeModelYear({ form, setField, setMake, modelOptions }) {
  const chevronBg = {
    backgroundImage:
      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230055FF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")",
    backgroundSize: "20px",
  };
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <div>
        <label className="text-xs font-medium uppercase tracking-widest text-slate-500">Make *</label>
        <select
          value={form.make}
          onChange={(e) => setMake(e.target.value)}
          data-testid="quote-select-make"
          style={chevronBg}
          className="mt-2 w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg appearance-none bg-no-repeat bg-[right_1rem_center] pr-12"
        >
          <option value="">Select make…</option>
          {CAR_MAKE_NAMES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium uppercase tracking-widest text-slate-500">Model *</label>
        <select
          value={form.model}
          onChange={(e) => setField("model", e.target.value)}
          disabled={!form.make}
          data-testid="quote-select-model"
          style={chevronBg}
          className="mt-2 w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg appearance-none bg-no-repeat bg-[right_1rem_center] pr-12 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          <option value="">{form.make ? "Select model…" : "Pick a make first"}</option>
          {modelOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs font-medium uppercase tracking-widest text-slate-500">Year *</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={form.car_year}
          onChange={(e) => setField("car_year", e.target.value.replace(/\D/g, ""))}
          placeholder="e.g. 2008"
          data-testid="quote-input-car_year"
          className="mt-2 w-full bg-white border-2 border-slate-300 px-4 py-3.5 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg"
        />
      </div>
    </div>
  );
}

function OptionsInput({ step, form, setField, onPicked }) {
  return (
    <div className="grid gap-3 sm:gap-4">
      {step.options.map((opt) => {
        const selected = form[step.name] === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              // Single-click advance: set the value AND trigger reveal in the
              // same synchronous handler. onPicked() calls advanceFromOption
              // which does NOT read form state, so no stale-closure bug.
              setField(step.name, opt.value);
              setTimeout(() => onPicked && onPicked(), 180);
            }}
            data-testid={`quote-option-${step.name}-${opt.value.replace(/\s+/g, "-").toLowerCase()}`}
            className={`group w-full flex items-center justify-between gap-5 px-5 sm:px-6 py-4 sm:py-5 border-2 text-left transition-all rounded-2xl ${
              selected
                ? "border-[#0055FF] bg-[#0055FF]/5 shadow-sm"
                : "border-slate-200 bg-white hover:border-slate-900 hover:shadow-md hover:-translate-y-0.5"
            }`}
          >
            <div className="flex items-center gap-4 sm:gap-5 min-w-0">
              <Illus Icon={opt.Icon} tone={opt.tone} spin={opt.spin} />
              <span className="text-base sm:text-lg font-medium text-slate-900 leading-tight">
                {opt.label}
              </span>
            </div>
            <span
              className={`h-7 w-7 grid place-items-center border-2 rounded-full shrink-0 transition-colors ${
                selected
                  ? "border-[#0055FF] bg-[#0055FF] text-white"
                  : "border-slate-300 group-hover:border-slate-500"
              }`}
            >
              {selected && <Check className="h-4 w-4" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SuccessScreen() {
  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="quote-success">
      <Header />
      <main className="flex-1 grid place-items-center">
        <section className="max-w-2xl mx-auto px-6 py-20 text-center animate-question-in">
          <div className="mx-auto h-20 w-20 grid place-items-center bg-[#0055FF] text-white rounded-full">
            <PartyPopper className="h-9 w-9" />
          </div>
          <h2 className="mt-8 font-display font-semibold text-4xl tracking-tight">
            Quote request received
          </h2>
          <p className="mt-4 text-slate-600 max-w-md mx-auto text-lg">
            Our team is reviewing the details now. You'll get a call from MR
            Crusher with your cash offer shortly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/info"
              className="inline-flex items-center justify-center gap-2 border-2 border-slate-300 hover:bg-slate-50 px-6 py-3 text-sm font-medium rounded-full transition-colors"
            >
              Back to home
            </Link>
            <a
              href="tel:+12893004040"
              className="inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] text-white px-6 py-3 text-sm font-medium rounded-full transition-colors"
            >
              Call us now · (289) 300-4040
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
